/**
 * The Admin menu, grouped by the job being done (D-325).
 *
 * It was one flat list of 36 links in the order each phase happened to add
 * them, and it showed everything to everybody: a cashier saw QuickBooks, Payouts
 * and Audit, and each one answered "not permitted" when clicked.
 *
 * Now every link names the permission its page already checks, and a link the
 * person cannot use is not drawn. The permission is written here, beside the
 * link, and must match the `can()` at the top of the page; `nav.test.ts` holds
 * the two together, so the menu and the page cannot disagree about who may go
 * in.
 *
 * A link can also name the **feature** it belongs to (D-369). QuickBooks and the
 * previous system's import are hidden behind switches in Admin → Settings, and a
 * link to a hidden feature is not drawn for anybody.
 */

/** Shown to anybody holding one of these. `null` is everybody signed in. */
export type NavPermission = string | readonly string[] | null;

export interface NavItem {
  to: string;
  label: string;
  /** Exact match only. Left false for a page with sub-pages or tabs, so it stays lit on them. */
  end: boolean;
  permission: NavPermission;
  /** Drawn only while this feature is on (D-369). */
  feature?: 'quickbooks' | 'history_import';
}

/** The features a link can depend on; anything missing counts as off. */
export type NavFeatures = Partial<Record<NonNullable<NavItem['feature']>, boolean>>;

export interface NavGroup {
  id: string;
  label: string;
  items: readonly NavItem[];
}

const REPORTS = ['reports.view_sales', 'reports.view_financial', 'reports.view_inventory', 'reports.view_attendance'] as const;

export const NAV_GROUPS: readonly NavGroup[] = [
  {
    id: 'box-office',
    label: 'Box office',
    items: [
      { to: '/', label: 'Home', end: true, permission: null },
      { to: '/orders', label: 'Orders', end: false, permission: 'orders.view' },
      { to: '/door', label: 'Door', end: true, permission: 'reports.view_attendance' },
      { to: '/fulfillment', label: 'Mail & will call', end: true, permission: 'orders.view' },
      { to: '/assistive-devices', label: 'Listening devices', end: true, permission: 'events.view' },
    ],
  },
  {
    id: 'on-sale',
    label: 'What’s on sale',
    items: [
      { to: '/events', label: 'Events', end: false, permission: 'events.view' },
      { to: '/series', label: 'Series', end: true, permission: 'events.view' },
      // The teams home games are played against (D-368).
      { to: '/opponents', label: 'Opponents', end: true, permission: 'events.view' },
      // Build a season from its schedule, price it on one grid, publish in bulk (D-371).
      { to: '/seasons', label: 'Seasons', end: true, permission: 'events.manage' },
      { to: '/packages', label: 'Packages', end: false, permission: 'events.view' },
      // Any-one-game vouchers and gate-scanned season passes (D-373).
      { to: '/vouchers', label: 'Comp vouchers', end: true, permission: 'vouchers.manage' },
      { to: '/season-passes', label: 'Season passes', end: true, permission: 'season_passes.manage' },
      // Theme nights and specials, linked to games (D-377).
      { to: '/promotions', label: 'Promotions', end: false, permission: 'events.view' },
      // Discount codes; "Promotions" until D-377.
      { to: '/promo-codes', label: 'Promo codes', end: false, permission: 'promotions.manage' },
      { to: '/add-ons', label: 'Extras', end: true, permission: 'events.view' },
      // Inquiries, quotes, space holds and bookings for groups (D-376).
      { to: '/group-sales', label: 'Group sales', end: false, permission: 'group_sales.view' },
      { to: '/group-holds', label: 'Group holds', end: true, permission: 'group_sales.view' },
      { to: '/holds', label: 'Holds', end: true, permission: 'inventory.view' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    items: [
      { to: '/customers', label: 'Customers', end: false, permission: 'customers.lookup' },
      { to: '/organizations', label: 'Organizations', end: false, permission: 'organizations.view' },
      { to: '/donations', label: 'Donations', end: false, permission: 'donations.view' },
    ],
  },
  {
    id: 'money',
    label: 'Money',
    items: [
      { to: '/reports', label: 'Reports', end: false, permission: REPORTS },
      { to: '/balances', label: 'Balances', end: true, permission: 'orders.view' },
      { to: '/house-accounts', label: 'House accounts', end: true, permission: 'house_accounts.manage' },
      { to: '/box-office/shifts', label: 'Drawer shifts', end: false, permission: 'reports.view_financial' },
      { to: '/payouts', label: 'Payouts', end: false, permission: 'finance.reconciliation.view' },
      { to: '/disputes', label: 'Chargebacks', end: true, permission: 'disputes.view' },
      { to: '/quickbooks', label: 'QuickBooks', end: true, permission: 'finance.reconciliation.view', feature: 'quickbooks' },
    ],
  },
  {
    id: 'setup',
    label: 'Setup',
    items: [
      { to: '/venues', label: 'Venues', end: false, permission: 'events.view' },
      { to: '/fees', label: 'Fees & taxes', end: true, permission: 'events.view' },
      { to: '/ticket-designs', label: 'Ticket designs', end: false, permission: 'ticket_designs.view' },
      // Each event's sponsor logo, header image, wallet images and terms: not
      // the layout, which is Ticket designs. Called "Ticket templates" before.
      { to: '/ticket-templates', label: 'Ticket branding', end: true, permission: 'events.view' },
      { to: '/media', label: 'Media', end: true, permission: 'media.view' },
      { to: '/sponsors', label: 'Sponsors', end: true, permission: 'sponsors.view' },
      { to: '/hardware', label: 'Hardware', end: true, permission: 'devices.manage' },
    ],
  },
  {
    id: 'admin',
    label: 'Admin',
    items: [
      { to: '/staff', label: 'Staff', end: false, permission: 'staff.view' },
      { to: '/settings', label: 'Settings', end: false, permission: 'settings.manage' },
      { to: '/migration', label: 'Import', end: false, permission: 'migration.import', feature: 'history_import' },
      { to: '/system', label: 'System', end: true, permission: 'admin.access' },
      { to: '/audit', label: 'Audit', end: true, permission: 'audit.view' },
    ],
  },
  /*
   * STGD's own section (D-393). Its permission is platform-reserved: no role
   * holds it and no admin can grant it, so the team's staff never see this
   * group, admins included. Only somebody signed in as an address in the
   * Worker setting PLATFORM_STAFF_EMAILS does.
   */
  {
    id: 'platform',
    label: 'STGD',
    items: [{ to: '/platform', label: 'STGD platform', end: true, permission: 'platform.access' }],
  },
];

/**
 * Pages that used to be menu links of their own and are now tabs on the page
 * they belong to. The routes are unchanged, so every link and bookmark to them
 * still works.
 */
export interface NavTab {
  to: string;
  label: string;
}

export const STAFF_TABS: readonly NavTab[] = [
  { to: '/staff', label: 'Staff' },
  { to: '/staff/pending', label: 'Pending logins' },
];

export const SETTINGS_TABS: readonly NavTab[] = [
  { to: '/settings', label: 'Behavior switches' },
  { to: '/settings/branding', label: 'Branding' },
  { to: '/settings/terms', label: 'Terms' },
  { to: '/settings/email-wording', label: 'Email wording' },
  // The team's Stripe account (Stripe Connect, D-393).
  { to: '/settings/payments', label: 'Payments' },
];

/** Group sales (D-376): the pipeline, the board of spaces per game, the catalog and settings, the reports. */
export const GROUP_SALES_TABS: readonly NavTab[] = [
  { to: '/group-sales', label: 'Pipeline' },
  { to: '/group-sales/board', label: 'Board' },
  { to: '/group-sales/spaces', label: 'Spaces & packages' },
  { to: '/group-sales/reports', label: 'Reports' },
];

export function permitted(permission: NavPermission, can: (permission: string) => boolean): boolean {
  if (permission === null) return true;
  return typeof permission === 'string' ? can(permission) : permission.some(can);
}

/** The groups this person can use anything in, holding only the links they can use and the features that are on. */
export function visibleGroups(can: (permission: string) => boolean, features: NavFeatures = {}): NavGroup[] {
  return NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => permitted(item.permission, can) && (!item.feature || features[item.feature] === true)) })).filter(
    (group) => group.items.length > 0,
  );
}

/**
 * Which group the page being looked at belongs to, so that group is open.
 *
 * The longest matching link wins, so `/staff/pending` belongs to Staff and
 * `/box-office/shifts/abc` to Drawer shifts. Home matches only itself, or it
 * would claim every page.
 */
export function groupOf(pathname: string): string | null {
  let best: { id: string; length: number } | null = null;
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      const matches = item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`);
      if (matches && (!best || item.to.length > best.length)) best = { id: group.id, length: item.to.length };
    }
  }
  return best?.id ?? null;
}
